import { useCallback, useRef, useState } from 'react';

import validatePassword from 'bl/validation/validatePassword.bl';
import Button from 'components/form/Button.component';
import STORE from 'config/store.singleton';
import { useTranslation } from 'providers/GILT.provider';
import { withRcmNewPasswordRequired } from 'utils/cognito.util';
import logger from 'utils/logger.util';

import PopupModalContainer from 'common/PopupModal';
import PasswordInputField from 'onboarding/common/PasswordInputField';
import HeaderContainer from 'user/common/Header';

const L = logger('NewPasswordRequiredForm');

const ResetPassword = ({ onSubmit }) => {
  const { t } = useTranslation();

  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const confirmFieldRef = useRef(null);

  const isDisabled = !validatePassword({ value: password }) || password !== passwordConfirm || submitting;

  const handleSubmit = useCallback(() => {
    if (isDisabled || !onSubmit) return;
    setSubmitting(true);
    onSubmit(password)
      .then(() => {
        window.location.replace(`${window.location.origin}`);
      })
      .catch((e) => {
        setSubmitting(false);
        setError(t('changePassword.setPasswordError'));
        L.warn('onSubmit() > catch()', e);
      });
  }, [password, onSubmit, t, isDisabled]);

  const onPasswordChange = useCallback(
    (value, keyToUse) => {
      if (keyToUse === 'password') {
        setPassword(value);
        const confirm = confirmFieldRef.current;
        if (confirm && confirm.state.touched) {
          const errorMessage = value === confirm.state.inputValue ? '' : t('common.errors.validation.passwordNotMatch');
          confirm.setState({ errorMessage });
        }
      } else {
        setPasswordConfirm(value);
      }
    },
    [t],
  );

  const handleValidatePassword = useCallback((value) => (validatePassword({ value }) ? '' : t('common.errors.validation.passwordErrorMsg')), [t]);

  const handleValidateConfirmPassword = useCallback((value) => (value === password ? '' : t('common.errors.validation.passwordNotMatch')), [password, t]);

  const hideError = useCallback(() => setError(''), []);

  return (
    <div data-bem="ResetPassword" className="screen-wrap">
      <div data-bem="ResetPassword__header" className="header-bg" />
      <div data-bem="ResetPassword__contents" className="content-wrap">
        <HeaderContainer title="" description={t('changePassword.subtitle')} />
        {error && <PopupModalContainer popupTitle={t('common.error')} popupMessage={error} buttonText={t('common.buttons.okay')} buttonCallback={hideError} />}

        <div className="content login auto-height" style={{ paddingBottom: '40px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <PasswordInputField
            data={{
              id: 'PasswordInput',
              label: t('changePassword.passwordLabel'),
              type: 'password',
              placeholder: '●●●●●●●●',
              inputValue: '',
              inputMode: 'text',
            }}
            onChangeHandler={(value) => {
              onPasswordChange(value, 'password');
            }}
            validateBlur={handleValidatePassword}
            allowAutocomplete={false}
          />
          <PasswordInputField
            ref={confirmFieldRef}
            data={{
              id: 'PasswordInput2',
              label: t('changePassword.confirmPasswordLabel'),
              type: 'password',
              placeholder: '●●●●●●●●',
              inputValue: '',
              inputMode: 'text',
            }}
            onChangeHandler={(value) => {
              onPasswordChange(value, 'passwordConfirm');
            }}
            validateBlur={handleValidateConfirmPassword}
            allowAutocomplete={false}
          />
        </div>
        <footer className="footer">
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', float: 'inline-end', marginTop: '10px' }}>
              <Button variant="primary" disabled={isDisabled} onClick={handleSubmit} width="large">
                <span className="ic-sml ic-arrow-forward-white" style={{ placeSelf: 'center' }} />
              </Button>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

const ResetPasswordHoC = withRcmNewPasswordRequired(ResetPassword, STORE);

// bare component export for tests (bypasses withRcmNewPasswordRequired HOC)
export { ResetPassword as ResetPasswordForTest };
export default ResetPasswordHoC;
